import type {ReactElement} from 'react';
import {AiChatTestId} from '@/test-ids/ai-chat-test-id';
import {useEffect, useMemo, useRef} from 'react';
import type {AiChatWindowPromptBoxProps} from './ai-chat-window-prompt-box.types';
import styles from './ai-chat-window-prompt-box.module.scss';
import {AiPromptBox} from '@Components/ai-prompt-box/ai-prompt-box';
import {AiPromptBoxSize, PromptBoxState} from '@Components/ai-prompt-box/ai-prompt-box.types';
import {AiChatPromptBoxFooterButton, AiChatSource} from '@Components/ai-chat-window/ai-chat-window.types';
import type {RequestConfig} from '@Components/ai-chat-window/ai-chat-window.types';
import {getPromptBoxPlaceholder, getShownFooterButtons, getStartingPromptBoxAttachments} from '@Components/ai-chat-window/lib/ai-chat-prompt-box-options.library';
import {AiPromptBoxUploads} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-actions/ai-prompt-box-uploads/ai-prompt-box-uploads';
import {useAiPromptBox} from '@Components/ai-prompt-box/hooks/useAiPromptBox';
import {openChatDocument} from '@Components/ai-prompt-box/libraries/chat-document-open.library';
import {useProTipContext} from '@Components/ai-prompt-box/hooks/useProTipContext';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {useScrollToBottom} from '@Components/ai-chat-window/hooks/useScrollToBottom';
import {useScrollChatToBottomOnPromptFocus} from '@Components/ai-chat-window/hooks/useScrollChatToBottomOnPromptFocus';
import {buildRequestConfig, buildRequestPreferences} from '@Components/ai-chat-window/lib/ai-chat-messages.library';
import {useAiChatCallbacks} from '@AiChat/callbacks/ai-chat-callbacks.context';
import type {FooterLeftAction, FooterLeftActionProps} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-left-actions/ai-prompt-box-footer-left-actions.types';
import {checkDailyLimit} from '@Libraries/ai-chat.library';
import {useAiChatPromptBoxCoordinator} from '@Components/ai-chat-window/hooks/useAiChatPromptBoxCoordinator';
import {useAiChat} from '@/ai-chat/providers/ai-chat.context';
import {AiChatSeasonalPromptBoxDecorations} from '@AiChat/components/ai-chat-seasonal-prompt-box-decorations/ai-chat-seasonal-prompt-box-decorations';
import {useAudioLimit} from '@AiChat/hooks/useAudioLimit';
import {useAiChatSize} from '@AiChat/hooks/useAiChatSize';
import {useMicLoginCallback} from '@Components/ai-prompt-box/hooks/useMicLoginCallback';
import {ProgressiveBlurOverlay} from '@Components/progressive-blur-overlay/progressive-blur-overlay';
import {AiPromptBoxTemplatesButton} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-actions/ai-prompt-box-templates-button/ai-prompt-box-templates-button';
import {AiPromptBoxAspectRatioSelector} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-actions/ai-prompt-box-aspect-ratio-selector/ai-prompt-box-aspect-ratio-selector';
import {AiPromptBoxBrandKitSelector} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-actions/ai-prompt-box-brand-kit-selector/ai-prompt-box-brand-kit-selector';
import useDidUpdateEffect from '@Hooks/useDidUpdateEffect';

export function AiChatWindowPromptBox({...props}: AiChatWindowPromptBoxProps): ReactElement {
  const promptBox = useAiPromptBox(props.id);
  const {handleFocus} = useAudioLimit();
  const {state: promptBoxState, actions: aiPromptBoxActions} = promptBox;
  const {state: aiChatState, actions: aiChatActions} = useAiChat();
  const {onSelectionRemoved} = useAiChatCallbacks();

  useMicLoginCallback(props.id, aiChatActions.onLoginSuccessActions);

  const {showScrollButton, scrollToBottom} = useScrollToBottom({
    containerId: 'pmw-chat-messages-container',
  });
  const promptBoxContainerRef = useRef<HTMLDivElement>(null);
  const scrollChatToBottomOnPromptFocus = useScrollChatToBottomOnPromptFocus(promptBoxContainerRef, scrollToBottom);
  const {isSmall, isLarge} = useAiChatSize();

  const shouldShowScrollToBottomButton = (): boolean => {
    return props.shouldShowScrollToBottomButton && showScrollButton;
  };

  const shouldShowProgressiveBlur = (): boolean => {
    return Boolean(props.showProgressiveBlur && props.chatId);
  };

  const buildRequestConfigWithPreferences = (): RequestConfig | undefined => {
    return buildRequestConfig({preferences: buildRequestPreferences(promptBoxState.pinnedAspectRatio ?? null, promptBoxState.pinnedBrandKitId ?? null)});
  };

  const coordinator = useAiChatPromptBoxCoordinator({
    promptBoxId: props.id,
    chatId: props.chatId,
    isMessageInProgress: props.isMessageInProgress,
    promptBoxOptions: props.promptBoxOptions,
  });

  useEffect(() => {
    aiChatActions.registerPromptBoxCoordinator(coordinator);

    return (): void => {
      aiChatActions.registerPromptBoxCoordinator(null);
    };
  }, [aiChatActions.registerPromptBoxCoordinator, coordinator]);

  const createdChatIdsRef = useRef<Set<string>>(new Set());
  const prevChatIdRef = useRef<string | undefined>(props.chatId);

  useEffect(() => {
    if (props.chatId && !prevChatIdRef.current) {
      createdChatIdsRef.current.add(props.chatId);
    }
    prevChatIdRef.current = props.chatId;
  }, [props.chatId]);

  const isNewChat = props.chatId ? createdChatIdsRef.current.has(props.chatId) : true;

  const proTipContext = useProTipContext({
    messages: props.messages,
    chatId: props.chatId,
    attachmentItems: promptBoxState.attachments?.items ?? [],
    isNewChat,
    hasLoadingAttachments: (promptBoxState.attachmentLoaders?.length ?? 0) > 0,
    isMessageInProgress: props.isMessageInProgress,
    editableTextLayersAvailable: aiChatState.canUseEditableTextLayers,
  });

  useEffect(() => {
    void checkDailyLimit(aiPromptBoxActions.update);
  }, []);

  useEffect(() => {
    if (props.initialPinnedAspectRatio) {
      aiPromptBoxActions.update({pinnedAspectRatio: props.initialPinnedAspectRatio});
    }

    if (props.initialPinnedBrandKitId) {
      aiPromptBoxActions.update({pinnedBrandKitId: props.initialPinnedBrandKitId});
    }
  }, []);

  useEffect(() => {
    if (!isSmall) {
      return;
    }

    aiPromptBoxActions.update({
      size: AiPromptBoxSize.SMALL,
      placeholder: getPromptBoxPlaceholder(props.promptBoxOptions, 'pmwjs_describe_the_design_you_want_to_create_elipses'),
    });
  }, [isSmall]);

  useDidUpdateEffect(() => {
    if (props.promptBoxOptions?.placeholder === undefined) {
      return;
    }

    aiPromptBoxActions.update({placeholder: props.promptBoxOptions.placeholder});
  }, [props.promptBoxOptions?.placeholder]);

  const templateSearchTerm = props.chatId ? (aiChatState.getTemplateSearchTerm(props.chatId) ?? undefined) : undefined;
  const allowedFooterButtons = props.promptBoxOptions?.footerButtons;

  const footerLeftActions = useMemo<FooterLeftAction[]>(() => {
    const footerButtonActions: Record<AiChatPromptBoxFooterButton, FooterLeftAction> = {
      [AiChatPromptBoxFooterButton.ATTACHMENTS]: (actionProps: FooterLeftActionProps): ReactElement | null => (
        <AiPromptBoxUploads {...actionProps} renderAttachmentTooltip={isLarge} hideSelectorBorder />
      ),
      [AiChatPromptBoxFooterButton.TEMPLATES]: (actionProps: FooterLeftActionProps): ReactElement | null => (
        <AiPromptBoxTemplatesButton {...actionProps} searchTerms={templateSearchTerm} />
      ),
      [AiChatPromptBoxFooterButton.BRAND_KIT]: AiPromptBoxBrandKitSelector,
      [AiChatPromptBoxFooterButton.ASPECT_RATIO]: AiPromptBoxAspectRatioSelector,
    };

    return getShownFooterButtons(allowedFooterButtons).map((button) => footerButtonActions[button]);
  }, [isLarge, templateSearchTerm, allowedFooterButtons]);

  const getProgressBlurBackdrop = (): ReactElement => {
    return <ProgressiveBlurOverlay className={styles.progressiveBlurOverlay} horizontalFade />;
  };

  const getScrollToBottomButton = (): ReactElement => {
    return (
      <Icon
        className={`${styles.scrollToBottomIcon} ${!shouldShowScrollToBottomButton() ? styles.hiddenScrollButton : ''}`}
        icon={'icon-arrow-bottom'}
        type={IconType.SHADOW}
        size={IconSize.SIZE_ICON_16}
        shape={IconShape.CIRCLE}
        onClick={scrollToBottom}
      />
    );
  };

  const getPromptBoxSize = (): AiPromptBoxSize => {
    if (isSmall) {
      return AiPromptBoxSize.SMALL;
    }

    return AiPromptBoxSize.MEDIUM;
  };

  const getPromptBoxPlaceholderText = (): string => {
    if (aiChatState.promptBoxPlaceholderKey !== null && !props.chatId) {
      return getPromptBoxPlaceholder(props.promptBoxOptions, aiChatState.promptBoxPlaceholderKey);
    }

    if (props.hasChatHistory) {
      return getPromptBoxPlaceholder(props.promptBoxOptions, 'pmwjs_make_chaneges_or_create_something_new_ellipses');
    }

    return getPromptBoxPlaceholder(props.promptBoxOptions, 'pmwjs_describe_the_design_you_want_to_create_elipses');
  };

  const getHorizontalInsetClasses = (): string => {
    if (aiChatState.isPromptBoxOnly) {
      return '';
    }

    return styles.horizontalInset;
  };

  const getPromptBoxClasses = (): string => {
    const classes = ['radius-12', '_full-width', styles.promptBox];

    if (props.chatId) {
      classes.push(styles.hasMessages);
    }

    if (props.containerClassname) {
      classes.push(props.containerClassname);
    }

    return classes.join(' ');
  };

  const renderPromptBox = (): ReactElement => {
    return (
      <div ref={promptBoxContainerRef} data-floating-prompt-box className={`flex-v-row ${getHorizontalInsetClasses()} ${styles.floatingPromptBox} ${props.chatId ? styles.chatHasMessages : ''}`} style={{position: 'relative'}}>
        {shouldShowProgressiveBlur() ? getProgressBlurBackdrop() : null}
        {getScrollToBottomButton()}
        <AiChatSeasonalPromptBoxDecorations />
        <AiPromptBox
          id={props.id}
          inputTestId={AiChatTestId.PROMPT_INPUT}
          submitTestId={AiChatTestId.PROMPT_SUBMIT}
          initialConfig={{
            prompt: props.initialPrompt ?? '',
            size: getPromptBoxSize(),
            promptBoxState: PromptBoxState.IDLE,
            placeholder: getPromptBoxPlaceholderText(),
            attachments: getStartingPromptBoxAttachments(props.initialAttachments, props.promptBoxOptions),
            showDisclaimer: false,
            transcription: {showMic: true},
            source: aiChatState.landingSource ?? AiChatSource.AI_CHAT_PAGE,
            pinnedAspectRatio: props.initialPinnedAspectRatio,
            pinnedBrandKitId: props.initialPinnedBrandKitId,
          }}
          onGenerate={async () => {
            await props.onGenerate(promptBoxState.prompt, promptBoxState.attachments, buildRequestConfigWithPreferences());
          }}
          onRegenerate={(): Promise<void> => {
            props.onRegenerate(undefined, buildRequestConfigWithPreferences());
            return Promise.resolve();
          }}
          onStop={props.onStopGeneration}
          onDocumentClick={(attachment): void => {
            openChatDocument(attachment, aiChatActions.openPdf);
          }}
          onSelectionRemove={onSelectionRemoved}
          containerClasses={getPromptBoxClasses()}
          options={footerLeftActions}
          showProtips
          proTipContext={proTipContext}
          focus
          disableAutoActivate
          spellCheck
          onFocus={() => {
            handleFocus();
            void checkDailyLimit(aiPromptBoxActions.update);
            scrollChatToBottomOnPromptFocus();
          }}
        />
      </div>
    );
  };

  return renderPromptBox();
}
